import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react'
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'
import { cn } from '@/lib/utils'

// ─── Types ────────────────────────────────────────────────────────────────────

export type ToastKind = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  kind: ToastKind
  title: string
  message?: string
}

interface ShowOptions {
  description?: string
  duration?: number
}

interface ToastContextValue {
  show(kind: ToastKind, title: string, options?: ShowOptions): void
  dismiss(id: string): void
  success(title: string, options?: ShowOptions): void
  error(title: string, options?: ShowOptions): void
  info(title: string, options?: ShowOptions): void
  warning(title: string, options?: ShowOptions): void
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null)

// ─── Kind config ──────────────────────────────────────────────────────────────

const KIND_CONFIG: Record<
  ToastKind,
  {
    icon: typeof CheckCircle2
    border: string
    iconClass: string
    defaultDuration: number
  }
> = {
  success: {
    icon: CheckCircle2,
    border: 'border-l-emerald-500',
    iconClass: 'text-emerald-500',
    defaultDuration: 5000,
  },
  error: {
    icon: AlertCircle,
    border: 'border-l-rose-500',
    iconClass: 'text-rose-500',
    defaultDuration: 8000,
  },
  info: {
    icon: Info,
    border: 'border-l-blue-500',
    iconClass: 'text-blue-500',
    defaultDuration: 5000,
  },
  warning: {
    icon: AlertTriangle,
    border: 'border-l-amber-500',
    iconClass: 'text-amber-500',
    defaultDuration: 5000,
  },
}

// ─── Single toast card ────────────────────────────────────────────────────────

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const { icon: Icon, border, iconClass } = KIND_CONFIG[toast.kind]

  return (
    <div
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border border-slate-200 border-l-4 bg-white px-4 py-3 shadow-md',
        border,
      )}
      role="alert"
      aria-live="assertive"
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', iconClass)} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-900">{toast.title}</p>
        {toast.message ? (
          <p className="mt-0.5 text-xs text-slate-500">{toast.message}</p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="ml-1 shrink-0 rounded p-0.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const show = useCallback(
    (kind: ToastKind, title: string, options?: ShowOptions) => {
      const id = Math.random().toString(36).slice(2)
      const duration = options?.duration ?? KIND_CONFIG[kind].defaultDuration

      setToasts((prev) => [
        ...prev,
        { id, kind, title, message: options?.description },
      ])

      setTimeout(() => dismiss(id), duration)
    },
    [dismiss],
  )

  const success = useCallback(
    (title: string, options?: ShowOptions) => show('success', title, options),
    [show],
  )
  const error = useCallback(
    (title: string, options?: ShowOptions) => show('error', title, options),
    [show],
  )
  const info = useCallback(
    (title: string, options?: ShowOptions) => show('info', title, options),
    [show],
  )
  const warning = useCallback(
    (title: string, options?: ShowOptions) => show('warning', title, options),
    [show],
  )

  const value: ToastContextValue = { show, dismiss, success, error, info, warning }

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast container */}
      <div
        aria-label="Notifications"
        className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none"
      >
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastCard toast={toast} onDismiss={dismiss} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>')
  }
  return ctx
}

export default ToastProvider
